import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Edit, Pause, Play, CheckCircle, Eye, MessageSquare, Plus, Trash2 } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useAuth, authFetch } from "@/lib/auth";
import { api, INR } from "@/lib/marketplace";

const STATUS_LABEL = { active: "Active", paused: "Paused", sold: "Sold", draft: "Draft" };
const STATUS_COLOR = { active: "text-[#00cccc]", paused: "text-amber-400", sold: "text-zinc-400", draft: "text-zinc-500" };

export default function Dashboard() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null);
  const [newPrice, setNewPrice] = useState("");

  useEffect(() => { document.title = "My Listings — Biker Boulevard"; }, []);
  useEffect(() => {
    if (loading) return;
    if (!user) { setBusy(false); return; }
    (async () => {
      try {
        const r = await authFetch("/api/me/listings");
        if (r.ok) { const j = await r.json(); setItems(j.items || []); }
      } finally { setBusy(false); }
    })();
  }, [user, loading]);

  const patchListing = async (id, patch) => {
    const r = await authFetch(`/api/listings/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (r.ok) {
      const u = await r.json();
      setItems((it) => it.map((x) => x.listing_id === id ? u : x));
      setEditing(null);
    }
  };

  const deleteListing = async (id) => {
    if (!window.confirm("Delete this listing permanently? This removes the listing, its photos, and all related chats. This cannot be undone.")) return;
    const r = await authFetch(`/api/listings/${id}`, { method: "DELETE" });
    if (r.ok) setItems((it) => it.filter((x) => x.listing_id !== id));
  };

  if (loading || busy) {
    return <PageShell><div className="pt-32 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00cccc] mx-auto" /></div></PageShell>;
  }
  if (!user) {
    return (
      <PageShell>
        <div className="pt-32 max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold">Sign in to view your listings</h2>
          <p className="mt-2 text-sm text-zinc-400">You need a Biker Boulevard account to manage your bikes.</p>
          <button onClick={login} className="mt-6 px-6 py-3 rounded-full bg-[#00cccc] text-black font-semibold" data-testid="login-google">Continue with Google</button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="pt-24 pb-20 max-w-[1200px] mx-auto px-5 md:px-10" data-testid="dashboard-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// My Garage</span>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Hi {user.name?.split(" ")[0] || "Rider"} — <span className="text-gradient-cyan">your listings</span></h1>
          </div>
          <Link to="/list-my-bike" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#00cccc] text-black font-semibold" data-testid="new-listing-cta">
            <Plus className="w-4 h-4" /> New Listing
          </Link>
        </div>

        {items.length === 0 ? (
          <div className="p-10 rounded-2xl border border-white/10 bg-white/[0.03] text-center">
            <p className="text-lg font-semibold">You haven&apos;t listed any bikes yet.</p>
            <Link to="/list-my-bike" className="mt-5 inline-block px-5 py-2.5 rounded-full border border-[#00cccc]/40 text-[#00cccc]">List Your First Bike</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => {
              const cover = it.photo_ids?.[it.primary_photo_index] || it.photo_ids?.[0];
              return (
                <div key={it.listing_id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex flex-col sm:flex-row gap-4" data-testid={`my-listing-${it.listing_id}`}>
                  <div className="w-full sm:w-32 h-32 rounded-xl overflow-hidden bg-[#1A1E23] shrink-0">
                    {cover ? <img src={api.photoUrl(cover)} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">{it.registration_year} · {it.brand}</div>
                        <h3 className="text-lg font-semibold text-white truncate">{it.brand} {it.model} {it.variant || ""}</h3>
                      </div>
                      <span className={`text-xs font-mono uppercase tracking-widest ${STATUS_COLOR[it.status] || "text-zinc-500"}`}>{STATUS_LABEL[it.status] || it.status}</span>
                    </div>
                    <div className="mt-2 flex items-baseline gap-3">
                      {editing === it.listing_id ? (
                        <>
                          <input type="number" value={newPrice} onChange={(e) => setNewPrice(e.target.value)}
                            className="w-32 px-3 py-1.5 bg-[#15181C] border border-white/10 rounded-lg text-sm" />
                          <button onClick={() => patchListing(it.listing_id, { expected_price: parseInt(newPrice) })} className="px-3 py-1.5 bg-[#00cccc] text-black rounded-full text-xs font-semibold">Save</button>
                          <button onClick={() => setEditing(null)} className="text-xs text-zinc-500">Cancel</button>
                        </>
                      ) : (
                        <>
                          <span className="text-xl font-bold text-[#00cccc]">{INR(it.expected_price)}</span>
                          {it.valuation && <span className="text-[11px] text-zinc-500">BB {INR(it.valuation.low)}–{INR(it.valuation.high)}</span>}
                        </>
                      )}
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-[11px] text-zinc-400">
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{it.views || 0} views</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Link to={`/marketplace/${it.listing_id}`} className="px-3 py-1.5 rounded-full border border-white/20 text-xs hover:border-[#00cccc]/50">View</Link>
                      {editing !== it.listing_id && it.status !== "sold" && (
                        <button onClick={() => { setEditing(it.listing_id); setNewPrice(String(it.expected_price || "")); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 text-xs hover:border-[#00cccc]/50" data-testid={`edit-price-${it.listing_id}`}>
                          <Edit className="w-3 h-3" /> Edit Price
                        </button>
                      )}
                      {it.status === "active" && (
                        <button onClick={() => patchListing(it.listing_id, { status: "paused" })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 text-xs hover:border-amber-400/40" data-testid={`pause-${it.listing_id}`}>
                          <Pause className="w-3 h-3" /> Pause
                        </button>
                      )}
                      {it.status === "paused" && (
                        <button onClick={() => patchListing(it.listing_id, { status: "active" })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/20 text-xs hover:border-[#00cccc]/50">
                          <Play className="w-3 h-3" /> Resume
                        </button>
                      )}
                      {it.status !== "sold" && (
                        <button onClick={() => patchListing(it.listing_id, { status: "sold" })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#00cccc]/40 text-[#00cccc] text-xs" data-testid={`mark-sold-${it.listing_id}`}>
                          <CheckCircle className="w-3 h-3" /> Mark Sold
                        </button>
                      )}
                      <button onClick={() => deleteListing(it.listing_id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-500/40 text-red-400 text-xs hover:bg-red-500/10" data-testid={`delete-listing-${it.listing_id}`}>
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <main className="relative bg-[#0D0F12] text-white min-h-screen">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,204,204,0.06), transparent 50%), #0D0F12" }} />
        <div className="absolute inset-0 grid-bg opacity-10" />
      </div>
      {children}
      <Footer />
    </main>
  );
}
